'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/app/components/button';
import type { IconProps } from '@/app/components/icons';
import { Icon } from '@/app/components/icons';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import { ToastStyled } from '@/app/components/overlays/toast/styles';
import type { ToastVariantType } from '@/app/components/overlays/toast/types';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

const IconInfo = dynamic(
  () =>
    import('@/app/components/icons/common-actions/icon-info').then(
      (mod) => mod.IconInfo
    ),
  {
    ssr: false,
  }
);

const IconCheckCircle = dynamic(
  () =>
    import('@/app/components/icons/ui-actions/icon-check-circle').then(
      (mod) => mod.IconCheckCircle
    ),
  {
    ssr: false,
  }
);

const TOAST_DURATION = 3000;

export interface ToastProps {
  /**
   * (Optional) The classname for the toast.
   */
  className?: string;

  /**
   * (Optional) The theme of the toast.
   */
  theme?: ToastVariantType['theme'];

  /**
   * (Optional) The type of the toast.
   */
  type?: ToastVariantType['type'];

  /**
   * (Optional) The title of the toast.
   */
  title?: string;

  /**
   * (Optional) The description of the toast.
   */
  description?: string;

  /**
   * (Optional) Manual control the close state. Default is true.
   */
  autoClose?: boolean;

  /**
   * The callback function when the toast is closed.
   */
  onClose: () => void;
}

export const Toast = ({
  className,
  theme = 'light',
  type = 'info',
  title,
  description,
  autoClose = true,
  onClose,
}: ToastProps) => {
  const [show, setShow] = useState<boolean | undefined>(undefined);
  const toastRef = useRef<HTMLDivElement | null>(null);
  const { isTransitioning, className: transitionClassName } =
    useMountTransition({
      ref: toastRef,
      isMount: !!show,
      baseClassName: 'opacity-0 transition-opacity duration-300',
      animateClassName: 'opacity-100',
    });

  const iconProps = useMemo<IconProps>(() => {
    switch (type) {
      case 'success':
        return {
          className: 'text-green-500',
          icon: <IconCheckCircle />,
        };
      case 'error':
        return {
          className: 'text-red-500',
          icon: <IconInfo />,
        };
      default:
        return {
          className: 'text-blue-400',
          icon: <IconInfo />,
        };
    }
  }, [type]);

  const handleCloseByKeyboard = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShow(false);
    }
  };

  useEffect(() => {
    if (show === undefined) return;
    if (isTransitioning || show) return;

    safeCall(onClose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransitioning]);

  useEffect(() => {
    setShow(true);
    if (!autoClose) return;

    const timer = setTimeout(() => {
      setShow(false);
    }, TOAST_DURATION);
    return () => {
      timer && clearTimeout(timer);
    };
  }, [autoClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleCloseByKeyboard);
    return () => {
      document.removeEventListener('keydown', handleCloseByKeyboard);
    };
  });

  if (!isTransitioning) {
    return null;
  }

  return createPortal(
    <div
      ref={toastRef}
      role="alert"
      className={ToastStyled({
        theme,
        type,
        className: cn(transitionClassName, className),
      })}
      tabIndex={-1}
      {...createDataAttributes({ test: 'toast' })}
    >
      <Icon {...iconProps} />
      <div className="flex-1 overflow-hidden break-all">
        {!!title && <p className="font-noto-bold text-body-1-fixed">{title}</p>}
        {!!description && <p className="text-body-2-fixed">{description}</p>}
      </div>
      <Button
        size="sm"
        className="size-10 text-gray-400 hover:text-gray-700"
        variant="tertiary"
        color="black"
        iconBefore={<IconClose />}
        onClick={() => setShow(false)}
      />
    </div>,
    document.body
  );
};
